/**
 * AI features API (estimates, insights, classify, stats)
 */

import type {
  TimeEstimateResult,
  InsightsResponse,
  ClassificationResult,
  EnrichmentStats,
} from '../types';
import { fetchApi } from './fetch';

export interface JobEnqueueResult {
  groupId: string;
  jobIds: string[];
  enqueued: number;
  message: string;
}

export interface JobResult {
  jobId: string;
  taskId: string;
  status: 'pending' | 'active' | 'completed' | 'failed' | 'cancelled';
  result?: unknown;
  error?: string;
}

export interface GroupStatus {
  groupId: string;
  total: number;
  completed: number;
  failed: number;
  pending: number;
  results: JobResult[];
}

export async function enqueueTimeEstimates(options: {
  taskIds?: string[];
  batchSize?: number;
  customInstructions?: string;
}): Promise<JobEnqueueResult> {
  return fetchApi('/ai/estimateTime', {
    method: 'POST',
    body: JSON.stringify(options),
  });
}

export async function enqueueClassification(options: {
  taskIds?: string[];
  batchSize?: number;
  customInstructions?: string;
}): Promise<JobEnqueueResult> {
  return fetchApi('/ai/classify', {
    method: 'POST',
    body: JSON.stringify(options),
  });
}

export async function pollJobGroup(groupId: string): Promise<GroupStatus> {
  return fetchApi(`/ai/jobs/${groupId}`);
}

export async function cancelJobGroup(groupId: string): Promise<{ cancelled: number }> {
  return fetchApi(`/ai/jobs/${groupId}`, { method: 'DELETE' });
}

/**
 * @deprecated Use enqueueTimeEstimates + pollJobGroup instead.
 * Kept for backward compatibility with AdvancedSettings until UI is updated.
 */
export async function generateTimeEstimates(options?: {
  taskIds?: string[];
  batchSize?: number;
  customInstructions?: string;
}): Promise<{
  results: TimeEstimateResult[];
  tasksProcessed: number;
  tasksClassified: number;
  duration: number;
}> {
  if (!options?.taskIds?.length) {
    // Legacy path: fetch all tasks needing estimates then enqueue
    return { results: [], tasksProcessed: 0, tasksClassified: 0, duration: 0 };
  }
  const { groupId } = await enqueueTimeEstimates({ taskIds: options.taskIds, customInstructions: options.customInstructions });
  // Poll until done (for backward compat callers that expect synchronous result)
  let status = await pollJobGroup(groupId);
  while (status.pending > 0) {
    await new Promise((r) => setTimeout(r, 800));
    status = await pollJobGroup(groupId);
  }
  return {
    results: status.results.filter((r) => r.status === 'completed').map((r) => r.result as TimeEstimateResult),
    tasksProcessed: status.total,
    tasksClassified: status.completed,
    duration: 0,
  };
}

export async function getProductivityInsights(
  days: number = 7,
  customInstructions?: string,
): Promise<InsightsResponse> {
  return fetchApi('/ai/insights', {
    method: 'POST',
    body: JSON.stringify({ days, customInstructions }),
  });
}

export async function getCachedInsights(days: number = 7): Promise<(InsightsResponse & { generatedAt: string }) | null> {
  return fetchApi(`/ai/insights?days=${days}`);
}

export async function classifyTasks(options?: {
  taskIds?: string[];
  force?: boolean;
  batchSize?: number;
  customInstructions?: string;
}): Promise<{
  results: ClassificationResult[];
  tasksProcessed: number;
  tasksClassified: number;
  duration: number;
}> {
  return fetchApi('/ai/classify', {
    method: 'POST',
    body: JSON.stringify(options || {}),
  });
}

export async function getEnrichmentStats(): Promise<EnrichmentStats> {
  return fetchApi('/stats/enrichment');
}

export interface DecomposeResult {
  taskId: string;
  task: { id: string; content: string };
  subtasks: Array<{ content: string; estimatedMinutes: number | null }>;
}

export async function decomposeTask(taskId: string): Promise<DecomposeResult> {
  return fetchApi('/ai/decompose', {
    method: 'POST',
    body: JSON.stringify({ taskId }),
  });
}

// ── Decompose Suggestions (auto-decompose feature) ──────────────────────────

export interface DecomposeSuggestion {
  id: string;
  taskId: string;
  suggestions: Array<{ content: string; estimatedMinutes: number }>;
  createdAt: string;
}

export async function getPendingDecomposeSuggestions(): Promise<DecomposeSuggestion[]> {
  // API returns { suggestions: DecomposeSuggestion[] } — extract the array.
  // Handle both shapes defensively to avoid "is not iterable" runtime crashes.
  const result = await fetchApi<{ suggestions?: DecomposeSuggestion[] } | DecomposeSuggestion[]>(
    '/decompose-suggestions',
  );
  if (Array.isArray(result)) return result;
  return (result as { suggestions?: DecomposeSuggestion[] }).suggestions ?? [];
}

export async function getDecomposeSuggestionForTask(taskId: string): Promise<DecomposeSuggestion | null> {
  try {
    return await fetchApi<DecomposeSuggestion>(`/decompose-suggestions/${taskId}`);
  } catch (err: unknown) {
    if (err instanceof Error && err.message?.includes('404')) return null;
    throw err;
  }
}

export async function dismissDecomposeSuggestion(id: string): Promise<void> {
  await fetchApi<void>(`/decompose-suggestions/${id}/dismiss`, { method: 'POST' });
}

export async function getProductivityStats(days: number = 7): Promise<{
  period: string;
  totalCompleted: number;
  withTimeTracking: number;
  averageMinutes: number | null;
  byCategory: Record<string, number>;
  byDay: Record<string, number>;
  trends?: {
    direction: 'up' | 'down' | 'stable';
    percentageChange: number;
    comparedTo: string;
  };
}> {
  return fetchApi(`/stats/productivity?days=${days}`);
}

export interface AiCostEstimate {
  estimatedCost: number;
  estimatedCostFormatted: string;
  estimatedDurationSeconds: number;
  model: string;
  tokens: { input: number; output: number };
  requiresConfirmation: boolean;
}

export interface AiEstimateRequest {
  operation: 'classify' | 'embed' | 'insights' | 'brain-dump' | 'decompose';
  scope: 'single' | 'all';
  taskCount?: number;
}

export async function fetchAiCostEstimate(req: AiEstimateRequest): Promise<AiCostEstimate> {
  return fetchApi<AiCostEstimate>('/ai/estimate', {
    method: 'POST',
    body: JSON.stringify(req),
  });
}

// ── Semantic Search ──────────────────────────────────────────────────────────

export interface SemanticSearchResult {
  results: import('../types').Task[];
  mode: 'semantic' | 'keyword';
  total: number;
}

export async function semanticSearch(query: string, limit = 10): Promise<SemanticSearchResult> {
  return fetchApi<SemanticSearchResult>('/ai/search', {
    method: 'POST',
    body: JSON.stringify({ query, limit }),
    headers: { 'Content-Type': 'application/json' },
  });
}
